import React, { useEffect, useRef, useCallback } from 'react';
import { Search, X, Clock, TrendingUp, Loader2 } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchProductDetails, fetchSimilarProducts, setFilters, fetchProductsByFilters } from "../../../redux/slices/productsSlice";

const Searchbar = () => {
    const [searchTerm, setSearchTerm] = React.useState('');
    const [isOpen, setIsOpen] = React.useState(false);
    const [showSuggestions, setShowSuggestions] = React.useState(false);
    const [suggestions, setSuggestions] = React.useState([]);
    const [recentSearches, setRecentSearches] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [selectedIndex, setSelectedIndex] = React.useState(-1);
    
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);
    const debounceRef = useRef(null);

    // Your actual products
    const products = [
        'Tshirt',
        'Oversized Tshirt', 
        'Tie Dye Oversized Tshirt',
        'Acid Wash Oversized Tshirt',
        'Essentials',
        'Hoodies'
    ];

    // Trending searches based on your products
    const trendingSearches = ['oversized tshirt', 'hoodies', 'tie dye', 'acid wash', 'tshirt','essentials'];

    // Load recent searches from localStorage on component mount
    useEffect(() => {
        const saved = localStorage.getItem('recentSearches');
        if (saved) {
            try {
                setRecentSearches(JSON.parse(saved));
            } catch (error) {
                console.error('Error loading recent searches:', error);
            }
        }
    }, []);

    // Save recent searches to localStorage
    const saveToRecentSearches = useCallback((term) => {
        if (!term.trim()) return;
        
        const updated = [term, ...recentSearches.filter(item => item !== term)].slice(0, 5);
        setRecentSearches(updated);
        try {
            localStorage.setItem('recentSearches', JSON.stringify(updated));
        } catch (error) {
            console.error('Error saving recent searches:', error);
        }
    }, [recentSearches]);

    // Generate suggestions based on your actual products
    const fetchSuggestions = useCallback(async (query) => {
        if (!query.trim() || query.length < 2) {
            setSuggestions([]);
            return;
        }

        setLoading(true);
        
        try {
            // Simulate API delay for better UX
            await new Promise(resolve => setTimeout(resolve, 150));
            
            const queryLower = query.toLowerCase();
            const productSuggestions = [];
            
            // Direct product matches
            products.forEach((product, index) => {
                if (product.toLowerCase().includes(queryLower)) {
                    productSuggestions.push({
                        id: `product-${index}`,
                        text: product,
                        type: 'product',
                        count: Math.floor(Math.random() * 50) + 10
                    });
                }
            });
            
            // Add variations and related searches
            const variations = [];
            
            if (queryLower.includes('t') || queryLower.includes('shirt')) {
                if (!variations.some(v => v.text.includes('oversized'))) {
                    variations.push({ text: `${query} oversized`, type: 'variation' });
                }
                if (!variations.some(v => v.text.includes('tie dye'))) {
                    variations.push({ text: `tie dye ${query}`, type: 'variation' });
                }
                if (!variations.some(v => v.text.includes('acid wash'))) {
                    variations.push({ text: `acid wash ${query}`, type: 'variation' });
                }
                 if (!variations.some(v => v.text.includes('essentials'))) {
                    variations.push({ text: `essentials ${query}`, type: 'variation' });
                }
            }
            
            if (queryLower.includes('over') || queryLower.includes('size')) {
                if (!variations.some(v => v.text.includes('tshirt'))) {
                    variations.push({ text: `oversized tshirt`, type: 'variation' });
                }
                if (!variations.some(v => v.text.includes('tie dye'))) {
                    variations.push({ text: `tie dye oversized tshirt`, type: 'variation' });
                }
            }
            
            if (queryLower.includes('hood')) {
                if (!variations.some(v => v.text.includes('oversized'))) {
                    variations.push({ text: `oversized hoodies`, type: 'variation' });
                }
                if (!variations.some(v => v.text.includes('pullover'))) {
                    variations.push({ text: `hoodies pullover`, type: 'variation' });
                }
            }
            
            if (queryLower.includes('tie') || queryLower.includes('dye')) {
                if (!variations.some(v => v.text === 'tie dye oversized tshirt')) {
                    variations.push({ text: `tie dye oversized tshirt`, type: 'variation' });
                }
            }
            
            if (queryLower.includes('acid') || queryLower.includes('wash')) {
                if (!variations.some(v => v.text === 'acid wash oversized tshirt')) {
                    variations.push({ text: `acid wash oversized tshirt`, type: 'variation' });
                }
            }
            
            // Add color variations
            const colors = ['black', 'white', 'gray', 'blue', 'green'];
            colors.forEach(color => {
                if (queryLower.includes(color) || queryLower.includes(color.substring(0, 3))) {
                    if (!variations.some(v => v.text.includes(color))) {
                        variations.push({ text: `${color} ${query}`, type: 'color' });
                    }
                }
            });
            
            // Convert variations to suggestion format and remove duplicates
            const variationSuggestions = variations
                .filter((v, index, arr) => 
                    arr.findIndex(item => item.text.toLowerCase() === v.text.toLowerCase()) === index
                )
                .filter(v => v.text.toLowerCase() !== queryLower && v.text.trim() !== query.trim())
                .slice(0, 4)
                .map((variation, index) => ({
                    id: `variation-${index}`,
                    text: variation.text,
                    type: variation.type,
                    count: Math.floor(Math.random() * 30) + 5
                }));
            
            // Combine and limit suggestions
            const allSuggestions = [...productSuggestions, ...variationSuggestions]
                .slice(0, 6);
            
            setSuggestions(allSuggestions);
            
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    }, [products]);

    // Debounced search function
    const debouncedSearch = useCallback((query) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        
        debounceRef.current = setTimeout(() => {
            fetchSuggestions(query);
        }, 300);
    }, [fetchSuggestions]);

    // Handle input change
    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setSelectedIndex(-1);
        
        if (value.trim().length >= 1) {
            debouncedSearch(value);
            setShowSuggestions(true);
        } else {
            setSuggestions([]);
            setShowSuggestions(true); // Show recent/trending when empty
        }
    };

    // Handle search toggle
    const handleSearchToggle = () => {
        const newIsOpen = !isOpen;
        setIsOpen(newIsOpen);
        
        if (newIsOpen) {
            setTimeout(() => {
                inputRef.current?.focus();
                if (!searchTerm.trim()) {
                    setShowSuggestions(true);
                }
            }, 100);
        } else {
            setShowSuggestions(false);
            setSearchTerm('');
            setSuggestions([]);
            setSelectedIndex(-1);
        }
    };

    // Handle search submission
    const handleSearch = (e, searchQuery = searchTerm) => {
        if (e) e.preventDefault();
        
        const finalQuery = searchQuery.trim();
        if (!finalQuery) return;
        
        saveToRecentSearches(finalQuery);
        dispatch(setFilters({ search: finalQuery }));
        dispatch(fetchProductsByFilters({ search: finalQuery }));
        navigate(`/collections/all?search=${encodeURIComponent(finalQuery)}`);
        
        // Reset all states
        setIsOpen(false);
        setShowSuggestions(false);
        setSearchTerm('');
        setSuggestions([]);
        setSelectedIndex(-1);
    };

    // Handle suggestion selection
    const handleSuggestionSelect = (suggestionText) => {
        if (!suggestionText.trim()) return;
        
        setSearchTerm(suggestionText);
        setShowSuggestions(false);
        handleSearch(null, suggestionText);
    };

    // Get all suggestions for keyboard navigation
    const getAllSuggestions = useCallback(() => {
        const allSuggestions = [];
        
        if (searchTerm.trim().length > 0) {
            // Add current suggestions
            allSuggestions.push(...suggestions.map(s => s.text));
        } else {
            // Add recent searches when input is empty
            allSuggestions.push(...recentSearches);
            // Add trending searches
            allSuggestions.push(...trendingSearches);
        }
        
        return allSuggestions;
    }, [suggestions, recentSearches, trendingSearches, searchTerm]);

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (!showSuggestions) return;
        
        const allSuggestions = getAllSuggestions();
        const totalItems = allSuggestions.length;
        
        if (totalItems === 0) return;
        
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => (prev + 1) % totalItems);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => prev <= 0 ? totalItems - 1 : prev - 1);
        } else if (e.key === 'Enter' && selectedIndex >= 0 && selectedIndex < totalItems) {
            e.preventDefault();
            handleSuggestionSelect(allSuggestions[selectedIndex]);
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
            setSelectedIndex(-1);
        }
    };

    // Handle clicking outside to close suggestions
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
                inputRef.current && !inputRef.current.contains(event.target)) {
                setShowSuggestions(false);
                setSelectedIndex(-1);
            }
        };

        if (showSuggestions) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showSuggestions]);

    // Clean up debounce on unmount
    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    // Render suggestion item
    const renderSuggestionItem = (text, index, icon, label) => (
        <div
            key={`suggestion-${index}-${text}`}
            className={`flex items-center px-4 py-3 cursor-pointer transition-colors ${
                selectedIndex === index ? 'bg-gray-100' : 'hover:bg-gray-50'
            }`}
            onClick={() => handleSuggestionSelect(text)}
        >
            <div className="mr-3 text-gray-400">
                {icon}
            </div>
            <div className="flex-1">
                <span className="text-gray-900">{text}</span>
            </div>
            {label && (
                <span className="text-xs text-gray-500 ml-2">{label}</span>
            )}
        </div>
    );

    // Calculate current index for rendering
    let currentIndex = 0;

    return (
        <div className={`flex items-center justify-center w-full transition-all duration-300 
            ${isOpen ? "absolute top-20 left-0 w-full bg-white h-auto z-50" : "w-auto"}`}>
            
            {isOpen ? (
<div className="relative flex flex-col items-center justify-center w-full">
    <form onSubmit={handleSearch} className="relative flex items-center justify-center w-full py-6 px-4 sm:px-6">
        <div className="relative w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-4 sm:mx-0" ref={suggestionsRef}>
            <input
                ref={inputRef}
                type="text"
                placeholder="Search products, color, categories..."
                value={searchTerm}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setShowSuggestions(true)}
                className="bg-gray-100 px-4 py-3 pl-4 pr-12 rounded-lg focus:outline-none focus:ring-2 focus:ring-black w-full
                    placeholder:text-gray-500 text-gray-900 text-sm sm:text-base"
                autoComplete="off"
            />
            
            {/* Search icon */}
            <button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gray-200 p-2 rounded-full hover:bg-gray-300 transition-colors
                    focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1"
            >
                <Search className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>


                            {/* Suggestions dropdown */}
                            {showSuggestions && (
                                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-96 overflow-y-auto z-50">
                                    {loading && (
                                        <div className="flex items-center justify-center py-4">
                                            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                                        </div>
                                    )}

                                    {!loading && (
                                        <>
                                            {/* Search suggestions */}
                                            {suggestions.length > 0 && (
                                                <div className="border-b border-gray-100">
                                                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                        Suggestions
                                                    </div>
                                                    {suggestions.map((suggestion) => {
                                                        const item = renderSuggestionItem(
                                                            suggestion.text, 
                                                            currentIndex, 
                                                            <Search className="h-4 w-4" />
                                                            `${suggestion.count} items`
                                                        );
                                                        currentIndex++;
                                                        return item;
                                                    })}
                                                </div>
                                            )}

                                            {/* Recent searches */}
                                            {recentSearches.length > 0 && searchTerm.length === 0 && (
                                                <div className="border-b border-gray-100">
                                                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                        Recent Searches
                                                    </div>
                                                    {recentSearches.map((recent) => {
                                                        const item = renderSuggestionItem(
                                                            recent, 
                                                            currentIndex, 
                                                            <Clock className="h-4 w-4" />
                                                        );
                                                        currentIndex++;
                                                        return item;
                                                    })}
                                                </div>
                                            )}

                                            {/* Trending searches */}
                                            {searchTerm.length === 0 && (
                                                <div>
                                                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                        Trending
                                                    </div>
                                                    {trendingSearches.map((trending) => {
                                                        const item = renderSuggestionItem(
                                                            trending, 
                                                            currentIndex, 
                                                            <TrendingUp className="h-4 w-4" />
                                                        );
                                                        currentIndex++;
                                                        return item;
                                                    })}
                                                </div>
                                            )}

                                            {/* No suggestions */}
                                            {suggestions.length === 0 && searchTerm.length > 0 && !loading && (
                                                <div className="px-4 py-6 text-center text-gray-500">
                                                    No suggestions found for "{searchTerm}"
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Close button */}
                        <button
                            type="button"
                            className="absolute right-10 top-1/2 transform -translate-y-1/2 bg-gray-200 p-2 rounded-full hover:bg-gray-300 transition-colors"
                            onClick={handleSearchToggle}
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </form>
                </div>
            ) : (
                <button onClick={handleSearchToggle}>
                    <Search className="h-6 w-6" />
                </button>
            )}
        </div>
    );
};

export default Searchbar;
